<template>
  <div class="flow-chart-container">
    <div :id="containerId" ref="container" />
    <RxkDialog
      v-model="showInfo"
      :show-cancel-btn="false"
      title="节点审批详情"
      width="700"
      append-to-body
      confirm-btn="知道了"
      @sure="showInfo = false"
    >
      <div class="tw-p-[16px]">
        <div
          class="tw-flex tw-my-[16px] tw-items-center"
          v-for="(item, index) in approveInfo?.logs"
          :key="index"
        >
          <span class="text-666 tw-mr-[10px]" v-if="item.levelName">{{ item.levelName }}</span>
          <StatusIcon class="tw-mr-[10px]" :status="item.action" />
          <span class="text-666 tw-mr-[10px]">{{ getName(item.operator, realName) }}</span>
          <el-tag
            :type="getStatus(item.action).type"
            class="tw-mr-[10px]"
            v-if="getStatus(item.action).name"
          >
            {{ getStatus(item.action).name }}
          </el-tag>
          <span class="text-666 tw-mr-[10px]">{{ getName(item.content, realName) }}</span>
          <span class="text-666 tw-mr-[10px]">{{ item.comment }}</span>
          <span class="text-aaa">{{ item.createTime }}</span>
        </div>
        <div v-if="approveInfo?.ccOperators?.length">
          <div class="tw-mt-[10px]" v-for="(cc, index) in approveInfo.ccOperators" :key="index">
            <span class="text-aaa tw-mr-[10px]">抄送：</span>
            <span class="text-666 tw-mr-[10px]">{{ cc }}</span>
            <span class="text-aaa tw-ml-[40px]">{{ approveInfo.createTime }}</span>
          </div>
        </div>
        <el-empty v-if="!approveInfo?.logs?.length" description="无审批记录" :image-size="60" />
      </div>
    </RxkDialog>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { createGraphRegister } from '@/hooks/Graph/useGraphRegister'
import { ApproveStatus, NodeType, StatusOptions } from '@/enums/flow'
import { graphDisabledOption } from '../config'
import { RxkDialog } from '@/components/common/RxkDialog'
import { deepMerge } from '@/utils/tools'
import { getName } from '@/components/business/FlowDeal/utils'

const props = defineProps({
  options: {
    type: Object,
    default: () => ({})
  },
  detail: {
    type: Object,
    default: () => ({})
  },
  refresh: Boolean,
  containerId: {
    type: String,
    default: 'flowDealContainer'
  }
})

const emits = defineEmits(['openChild', 'openApproval'])

const { graph, renderJson } = createGraphRegister(
  props.containerId,
  {
    options: deepMerge(graphDisabledOption, props.options, 'replace'),
    flowConfigData: {} as any
  },
  () => {}
)

watch(
  () => props.detail,
  val => {
    if (val?.style) {
      addListener()
      if (val.processType === 'BUSINESS') {
        formatBusinessGraphJson()
      } else {
        formatApproveGraphJson()
      }
    }
  },
  { deep: true }
)

watch(
  () => props.refresh,
  val => {
    if (val) {
      setTimeout(() => {
        resetSize()
      }, 100)
    }
  }
)

const realName = computed(() => {
  return props.detail?.userRealNameMap || {}
})

// 将画布设置为不可操作
function disabledGraph() {
  const nodes = graph.value.getNodes()
  const edges = graph.value.getEdges()
  nodes.forEach((node: any) => {
    const ports = node.getPorts() || []
    ports.forEach((port: any) => {
      node.setPortProp(port.id, 'attrs/circle/style/visibility', 'hidden')
    })
  })
  edges.forEach((edge: any) => {
    const node = graph.value.getCellById(edge.target.cell) || {}
    const date = node?.getData() || {}
    if (date.state === 'DONE') {
      edge.attr('line/stroke', '#5687ff')
    } else {
      edge.attr('line/stroke', '#ccc')
    }
  })
}

// 渲染画布数据 审批
async function formatApproveGraphJson() {
  const needChangeTypes = ['flow-approval-comp', 'flow-comp', 'rect']
  const list = JSON.parse(props.detail?.style)
  list.forEach((el: any) => {
    if (needChangeTypes.includes(el.shape) && el.data.flag !== 'END') {
      let data: any = {}
      // 替换展示节点
      if (el.shape === 'rect') {
        el.shape = 'flow-start'
        el.size = {
          width: 136,
          height: 62
        }
        el.position = {
          x: el.position.x - 25,
          y: el.position.y
        }
      } else {
        el.shape = 'flow-result'
        el.size = {
          width: 180,
          height: 75
        }
      }
      // nodeMap里面的值弄过来
      if (props.detail?.nodeMap?.nodes) {
        props.detail?.nodeMap?.nodes.forEach((item: any) => {
          if (item.index === el.id) {
            data = {
              state: item.state,
              guid: item.guid,
              operators:
                item?.approver
                  ?.filter((item: Recordable) => item.type !== 'DYNAMIC')
                  ?.map((el: any) => el.name) || [],
              ccOperators:
                item.ccOperators
                  ?.filter((item: Recordable) => item.type !== 'DYNAMIC')
                  .map((item: Recordable) => item.name) || []
            }
          }
        })
      }
      // 过滤掉动态参数
      // 从logs里面去取审批人
      data.logs = getApproveInfo(data?.guid, 'logs') || []
      data.ccOperators = getApproveInfo(data?.guid, 'ccOperators') || data.ccOperators
      data.createTime = getApproveInfo(data?.guid, 'createTime') || data.createTime || []
      data.operators = getApproveInfo(data?.guid, 'operators') || data.operators
      // 开始改变type
      if (el.data.flag === 'START') {
        data.type = NodeType.START
      }
      el.data = {
        ...el.data,
        ...data
      }
      console.log(data.approver, 'ccccc')
    }
  })
  renderJson(list)
  disabledGraph()
  setTimeout(() => {
    resetSize()
  }, 100)
}

// 渲染画布数据 业务
async function formatBusinessGraphJson() {
  const list = JSON.parse(props.detail?.style)
  list.forEach((el: any) => {
    if (el.shape === 'flow-comp') {
      el.shape = 'flow-business-result'
      el.size = {
        width: 180,
        height: 75
      }
      let data: any = {}
      // nodeMap里面的值弄过来
      if (props.detail?.nodeMap?.nodes) {
        props.detail?.nodeMap?.nodes.forEach((item: any) => {
          if (item.index === el.id) {
            data = {
              state: item.state,
              guid: item.guid,
              dealTime: item.updateTime,
              processGuid: item.processGuid,
              hasApprovalProcess: item.hasApprovalProcess,
              operators: getBusinessOperators(item.guid) // 获取节点审批信息
            }
          }
        })
      }
      data.readonly = true
      el.data = {
        ...el.data,
        ...data
      }
    }
  })
  renderJson(list)
  disabledGraph()
  setTimeout(() => {
    resetSize()
  }, 100)
}

function getBusinessOperators(guid: string) {
  const maps = props.detail?.nodeOperatorMap
  const userRealNameMap = props.detail?.userRealNameMap
  const operators = maps[guid] || []
  const names: string[] = []
  if (operators?.length) {
    operators.forEach((id: string) => {
      userRealNameMap[id] && names.push(userRealNameMap[id])
    })
  }
  return names
}

function getApproveInfo(id: string, key: string) {
  let info: any = null
  if (props.detail?.approvalLogs?.length) {
    props.detail.approvalLogs.forEach((item: any) => {
      if (item.nodeGuid === id) {
        info = item[key]
        if (key === 'ccOperators' || key === 'operators') {
          info = item[key]?.map((item: string) => getName(item, realName))
        }
      }
    })
  }
  return info
}

// 添加点击事件监听
function addListener() {
  graph.value.on('node:click', ({ node }: any) => {
    const data = node.getData()
    if (props.detail.processType === 'BUSINESS') {
      if (data.type === NodeType.CHILD) {
        // 如果是子流程就打开子流程得流程弹框
        emits('openChild', {
          parentProcessGuid: data.processGuid,
          parentNodeGuid: data.guid
        })
      }
      if (data.type === NodeType.BUSINESS || data.type === NodeType.PEER) {
        console.log(data, '222222222222')
        // 同级，业务节点，判断有没有审批流程id 有就打开审批流程
        if (data.hasApprovalProcess) {
          // setApproveInfo(data)
          emits('openApproval', {
            parentProcessGuid: data.processGuid,
            parentNodeGuid: data.guid
          })
        }
      }
    } else {
      // 获取弹框数据
      const actionTypes = [NodeType.BUSINESS, NodeType.PEER, NodeType.APPROVAL, NodeType.START]
      if (actionTypes.includes(data.type)) {
        showDialog(data)
      }
    }
  })
}

// 修改画布大小
function resetSize() {
  graph.value.centerContent()
}

// 显示点击弹出框
const showInfo = ref(false)
const approveInfo = ref<any[]>([])

function getStatus(status: ApproveStatus) {
  return StatusOptions[status] || {}
}

function showDialog(data: any) {
  showInfo.value = true
  approveInfo.value = data
}
</script>
<style lang="scss" scoped>
.flow-chart-container {
  width: 100%;
  height: 100%;
  #container {
    width: 100%;
    height: 100%;
  }
}

.text-aaa {
  color: #aaaaaa;
  font-size: 12px;
}

.text-333 {
  color: #333333;
  font-size: 14px;
}

.text-666 {
  color: #666666;
  font-size: 14px;
  font-style: normal;
}
.status-tag {
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  margin-right: 12px;
}
.icon-tag {
  font-size: 16px;
}

.text-tag {
  border-width: 1px;
  border-style: solid;
  border-radius: 16px;
}
:deep(.el-tag) {
  border: none;
}
</style>
